import { ComponentPortal, DomPortalOutlet } from '@angular/cdk/portal';
import { ApplicationRef, ComponentFactoryResolver, ComponentRef, Injectable, Injector } from '@angular/core';
import { isNotNullish } from '@examdojo/util/nullish';
import { GridApi, IRowNode, RowDragEvent } from 'ag-grid-community';
import { AssetTreeNode } from '../../model/asset-tree-node.model';
import { AssetExplorerService } from '../asset-explorer.service';
import { closestDirectory, getSourceDirectory } from '../asset-tree-builder';
import { AssetTreeDndTooltipComponent } from '../asset-tree-dnd-tooltip/asset-tree-dnd-tooltip.component';

@Injectable()
export class AssetTreeDnDService {
  constructor(
    private readonly assetExplorerService: AssetExplorerService,
    private readonly injector: Injector,
    private readonly componentFactoryResolver: ComponentFactoryResolver,
    private readonly appRef: ApplicationRef,
  ) {}

  private readonly dndTooltipPortal = new ComponentPortal(AssetTreeDndTooltipComponent);

  private portalOutlet?: DomPortalOutlet;
  private portalRef?: ComponentRef<AssetTreeDndTooltipComponent>;

  private potentialDropTarget: IRowNode<AssetTreeNode> | null = null;

  isPotentialDropTarget(node: IRowNode<AssetTreeNode>) {
    return !!this.potentialDropTarget && this.potentialDropTarget === node;
  }

  readonly onRowDragMove = (event: RowDragEvent<AssetTreeNode>) => {
    const sourceDirectoryNode = event.node.data && getSourceDirectory(event.node.data);
    const targetNode = (event.overNode?.data && closestDirectory(event.overNode.data)) ?? sourceDirectoryNode;

    const nodesAllowedToBeDragged = this.getNodesAllowedToBeDragged(event);

    const newPotentialParent =
      targetNode && nodesAllowedToBeDragged.length ? event.api.getRowNode(targetNode.path) ?? null : null;

    this.updatePotentialDropTarget(newPotentialParent, event.api);
    this.updatePortal({
      allowedToBeMoved: !!targetNode && !!nodesAllowedToBeDragged.length,
      nodes: event.nodes.map((node) => node.data).filter(isNotNullish),
    });
  };

  readonly onRowDragLeave = (event: RowDragEvent<AssetTreeNode>) => {
    this.updatePotentialDropTarget(null, event.api);
  };

  readonly onRowDragEnd = (event: RowDragEvent<AssetTreeNode>) => {
    const sourceDirectoryNode = event.node.data && getSourceDirectory(event.node.data);
    const targetNode = (event.overNode?.data && closestDirectory(event.overNode.data)) ?? sourceDirectoryNode;

    const nodesAllowedToBeDragged = this.getNodesAllowedToBeDragged(event);

    this.updatePotentialDropTarget(null, event.api);
    this.portalOutlet?.detach();
    this.portalOutlet = undefined;
    this.portalRef = undefined;

    if (!nodesAllowedToBeDragged.length || !targetNode) {
      return;
    }

    this.assetExplorerService.move(nodesAllowedToBeDragged, targetNode.path);
    event.api.getRowNode(targetNode.path)?.setExpanded(true);
  };

  private getNodesAllowedToBeDragged(event: RowDragEvent<AssetTreeNode>) {
    const sourceDirectoryNode = event.node.data && getSourceDirectory(event.node.data);
    const targetNode = (event.overNode?.data && closestDirectory(event.overNode.data)) ?? sourceDirectoryNode;

    if (!targetNode) {
      return [];
    }

    const nodesToDrag = event.nodes.map((n) => n.data).filter(isNotNullish);

    return this.assetExplorerService.getNodesAllowedToBeMoved(nodesToDrag, targetNode);
  }

  private updatePotentialDropTarget(node: IRowNode<AssetTreeNode> | null, api: GridApi<AssetTreeNode>) {
    if (this.potentialDropTarget === node) {
      return;
    }

    const nodes = [node, this.potentialDropTarget].filter(isNotNullish);

    this.potentialDropTarget = node;
    api.refreshCells({
      rowNodes: nodes,
      force: true,
    });
  }

  private updatePortal(data: AssetTreeDndTooltipData) {
    const agGridDndContainer = document.querySelector('.ag-dnd-ghost');
    if (!(agGridDndContainer instanceof HTMLElement)) {
      console.error('Could not find ag-grid DnD container.');
      return;
    }

    agGridDndContainer.classList.add('y42-asset-tree-dnd-tooltip-container');

    // Reattach the portal in case the dnd container was dropped outside of the drop zone.
    // In this case there is no way to detect such event as ag-grid does not fire any event on drop cancellation.
    if (this.portalOutlet && agGridDndContainer.querySelector('.ag-dnd-ghost-label')) {
      this.portalOutlet.detach();
      this.portalOutlet = undefined;
      this.portalRef = undefined;
    }

    if (!this.portalOutlet) {
      this.portalOutlet = new DomPortalOutlet(
        agGridDndContainer,
        this.componentFactoryResolver,
        this.appRef,
        this.injector,
      );
    }

    if (!this.portalRef) {
      // Attach our custom component via portal.
      this.portalRef = this.portalOutlet.attach(this.dndTooltipPortal);
    }

    // Pass data to the component.
    this.portalRef.instance.nodes = data.nodes;
    this.portalRef.instance.allowedToBeMoved = data.allowedToBeMoved;

    this.portalRef.changeDetectorRef.markForCheck();
  }
}

interface AssetTreeDndTooltipData {
  nodes: AssetTreeNode[];
  allowedToBeMoved: boolean;
}
